import {html, LitElement, PropertyValues} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {defaultSettings, Settings} from './models/fishman-forgot-password.model';
import {a11Fetch, A11SiteDataModel, FetchProps} from './utilities';

@customElement('fishman-forgot-password')
export class FishmanForgotPassword extends LitElement {

    @property({type: String})
    encodedSettings = '';

    @property({type: Object})
    settings: Settings = {...defaultSettings};

    @property({type: String})
    current_user_name = '';

    @property({type: String})
    default_username_value = '';

    @property({type: Boolean})
    is_form_submitting = false;

    @property({type: Boolean})
    is_successful = false;

    @property({type: String})
    error_message = '';

    override firstUpdated = () => {
        this.current_user_name = this.default_username_value;
    }

    override updated(changedProperties: PropertyValues) {
        if (!changedProperties.has('encodedSettings')) {
            return;
        }

        try {
            const rawSettings = atob(this.encodedSettings); // decode base64 string
            this.settings = JSON.parse(rawSettings); // parse the JSON string into an object
        } catch (e) {
            console.error('Cannot parse settings:', e);
        }
    }

    override render() {
        // Destructure the settings object for easy access to the properties
        const {username_field_label} = this.settings;

        if (this.is_successful) {
            return html`
                <div class="alert alert-success my-5" role="alert" data-test="fishman-forgot-password-success">
                    If this email exists, a password reset link was emailed to you. It may take a few minutes to arrive,
                    please check your
                    junk/spam folder as well. If the email does not arrive, try creating a new account.
                </div>

                <fishman-update-password .encodedSettings=${this.encodedSettings} .email="${this.current_user_name}"/>
            `;
        }

        return html`
            <form class="row py-3 g-3" @submit=${this.onSubmit}>
                <div class="col-12 d-flex flex-column gap-3">

                    <h1 class="fishman-forgot-password__section-title text-center">
                        Reset your password
                    </h1>

                    <div>
                        ${username_field_label ? html`<label for="username"
                                                             class="form-label">${username_field_label}</label>` : ''}
                        <input type="text" name="username" id="username" class="form-control"
                               .value=${this.default_username_value} @change=${this._handleUserNameChange}
                               data-test="username" required>
                    </div>

                    ${this.is_form_submitting ? html`
                        <div class="alert alert-info mb-0" role="alert"
                             data-test="fishman-forgot-password-validating-details">Validating your information...
                        </div>
                    ` : ''}

                    ${this.error_message !== '' ? html`
                        <div class="alert alert-danger mb-0" role="alert"
                             data-test="fishman-forgot-password-failed-invalid-information"
                             .innerHTML=${this.error_message}/>
                    ` : ''}

                    <p>
                        <button type="submit" class="btn btn-primary align-self-center"
                                ?disabled=${this.is_form_submitting}
                                data-test="submit">
                            Send password reset link
                        </button>
                    </p>

                    <p>
                        <button class="btn btn-primary-outline p-0 fs-6" type="button"
                                @click=${this._handleReturnToLoginClick}
                                data-test="fishman-login-return-to-login">Cancel
                        </button>
                    </p>

                </div>
            </form>
        `;
    }

    override createRenderRoot() {
        return this;
    }

    /**
     * Triggers a custom event to return the user to the login screen. The fishman-login component listens for this event and
     * will output the fishman-login component when this is fired. The fishman-login component also uses the current username value
     * so, it can populate the username field with what had been entered in the fishman-forgot-password component.
     *
     */
    private _handleReturnToLoginClick = () => {
        const event = new CustomEvent('fishman-login-return-to-login', {detail: {current_user_name: this.current_user_name}});
        this.dispatchEvent(event);
    }

    /**
     * Keeps the value of the username field in a property in the state. This allows the 'return to login' button to know
     * what the value of the username field is, and send that value back to the fishman-login component.
     *
     * @param {Event} e - The event object.
     */
    private _handleUserNameChange = (e: Event) => {
        this.current_user_name = (e.target as HTMLInputElement).value;
    }

    private async onSubmit(e: Event) {
        e.preventDefault();
        this.is_form_submitting = true;
        this.error_message = '';

        const {block_id} = this.settings;

        const form = e.target as HTMLFormElement;
        const formData = new FormData(form);

        const fetchProperties: FetchProps = {
            action: 'fishman_request_password_reset_via_ajax',
            block_id,
            method: 'POST',
            body: formData,
        };

        const {statusCode, errorMessage} = await a11Fetch(fetchProperties);

        if (statusCode === 200) {
            this.is_successful = true;
            return;
        }

        this.error_message = errorMessage;
        this.is_form_submitting = false;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'fishman-forgot-password': FishmanForgotPassword;
    }

    interface Window {
        ai_site_data: A11SiteDataModel
    }
}
